import { useEffect } from 'react';

import { _parseBoolean } from '@dynatrace/strato-components/core';

import { useFormFieldContext } from './useFormFieldContext.js';
import { useFormFieldMessagesContext } from './useFormFieldMessagesContext.js';
import type { FormControlBaseProps } from '../../../core/types/form-control-props.js';
import { FormFieldMessagesAction } from '../state/formfield-messages-state-reducer.js';
import { FormFieldAction } from '../state/formfield-state-reducer.js';

/** @internal */
export function useSyncedFormControlProps(formcontrolprops: {
  propDisabled?: boolean;
  propAriaDisabled?: boolean | 'true' | 'false';
  propRequired?: boolean;
  propId?: string;
}): Pick<FormControlBaseProps, 'disabled' | 'required' | 'id'> & {
  ariaDisabled?: boolean;
} {
  const { propDisabled, propRequired, propId, propAriaDisabled } =
    formcontrolprops;

  const {
    controlId,
    required: contextRequired,
    disabled: contextDisabled,
    ariaDisabled: contextAriaDisabled,
    dispatch,
  } = useFormFieldContext();

  const { dispatch: formFieldMessageContextDispatch } =
    useFormFieldMessagesContext();

  const disabled = propDisabled ?? contextDisabled;
  const ariaDisabled = _parseBoolean(propAriaDisabled) ?? contextAriaDisabled;
  const required = propRequired ?? contextRequired;
  const id = propId || controlId;

  // we are using the useEffect here to not run into a bad state error
  useEffect(() => {
    dispatch?.({
      type: FormFieldAction.UPDATE,
      payload: {
        id: propId,
        disabled: propDisabled,
        required: propRequired,
        ariaDisabled: _parseBoolean(propAriaDisabled),
      },
    });
  }, [dispatch, propDisabled, propId, propRequired, propAriaDisabled]);

  useEffect(() => {
    formFieldMessageContextDispatch?.({
      type: FormFieldMessagesAction.DISABLEMESSAGE,
      payload: { isDisabled: disabled },
    });
  }, [disabled, formFieldMessageContextDispatch]);

  return {
    disabled,
    required,
    ariaDisabled,
    id,
  };
}
